import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  TextField,
  MenuItem,
  Box,
  Typography,
  Chip,
  Button,
} from "@mui/material";
import {
  Build as ToolIcon,
  AccountTree as SubAgentIcon,
} from "@mui/icons-material";

import BottomActionBar from "../../components/BottomActionBar";
import PromptVariableAutocomplete from "../../components/PromptVariableAutocomplete/PromptVariableAutocomplete";
import LoadingSpinner from "../../components/LoadingSpinner";
import useApiCall from "../../hooks/useApiCall";
import { usePageMeta } from "../../contexts/PageMetaContext";
import { INDIGO, SLATE } from "../../constants/Colors";
import useToast from "../../hooks/useToast";

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

const FIELD_HINT_SX = {
  fontSize: "0.75rem",
  color: SLATE[400],
  mb: "6px",
  display: "block",
};

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

function SectionCard({ children }) {
  return (
    <Box sx={{ bgcolor: "#ffffff", border: `1px solid ${SLATE[200]}`, borderRadius: "16px", p: 3, mb: 2.5 }}>
      {children}
    </Box>
  );
}

function SectionHeader({ title, description }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: SLATE[900] }}>
        {title}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500], mt: "2px" }}>
          {description}
        </Typography>
      )}
    </Box>
  );
}

const WORKFLOW_TYPES = [
  { label: "LLM Agent", value: "llm_agent", description: "Single AI agent with capabilities" },
  { label: "Sequential Agent", value: "sequential_agent", description: "Executes sub-agents in sequence" },
  { label: "Parallel Agent", value: "parallel_agent", description: "Executes sub-agents in parallel" },
  { label: "Loop Agent", value: "loop_agent", description: "Executes sub-agents in a loop until condition met" },
];

const AGENT_TYPES = [
  { label: "Enrollment AI", value: "enrollment", description: "Turn inquiries into enrollments with automated guidance" },
  { label: "Retention AI", value: "retention", description: "Support enrolled students and drive completion" },
  { label: "Admissions AI", value: "admissions", description: "Guide students through complex admissions processes" },
  { label: "Financial Aid AI", value: "financial_aid", description: "Help with financial aid questions and processes" },
  { label: "Academic Advising", value: "academic_advising", description: "Assist with course selection and academic planning" },
  { label: "Career Services", value: "career_services", description: "Support career planning and job search" },
  { label: "E-commerce Support", value: "ecommerce_support", description: "Handle product inquiries, orders, and customer support" },
  { label: "Customer Support", value: "customer_service", description: "Provide 24/7 customer support" },
  { label: "Sales Agent", value: "sales", description: "Assist with sales inquiries and lead qualification" },
  { label: "Marketing Agent", value: "marketing", description: "Support marketing campaigns and customer engagement" },
  { label: "Technical Support", value: "technical_support", description: "Provide technical troubleshooting and help" },
  { label: "General Agent", value: "general", description: "General purpose agent for various tasks" },
  { label: "Transfer", value: "transfer", description: "Support transfer student inquiries" },
];

const STATUSES = ["draft", "active", "inactive", "archived"];

export default function AgentTemplateForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const tokens = useSelector((state) => state?.tokens.value);
  const { makeRequest } = useApiCall();
  const { setPageMeta } = usePageMeta();
  const { error: toastError } = useToast();
  const isEdit = Boolean(id);

  const INITIAL_FORM = {
    name: "",
    agent_type: "",
    agent_workflow_type: "llm_agent",
    system_prompt: "",
    status: "active",
    output_key: "response",
    input_keys: [],
    max_iterations: 10,
    termination_condition: {},
    input_schema: {},
    output_schema: {},
    metadata: {},
  };

  const originalDataRef = useRef(INITIAL_FORM);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [inputKeyText, setInputKeyText] = useState("");

  useEffect(() => {
    if (!isEdit) {
      setPageMeta({ description: "Create a master template that can be cloned for multiple clients." });
    }
    return () => setPageMeta({});
  }, [isEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isEdit) fetchTemplate();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTemplate = async () => {
    setFetchLoading(true);
    try {
      const response = await makeRequest(`/v2/agent_templates/${id}`, {
        method: "GET",
        authTokens: tokens,
      });
      const template = response.template || response;
      setFormData({
        name: template.name || "",
        agent_type: template.agent_type || "",
        agent_workflow_type: template.agent_workflow_type || "llm_agent",
        system_prompt: template.system_prompt || "",
        status: template.status || "active",
        output_key: template.output_key || "response",
        input_keys: template.input_keys || [],
        max_iterations: template.max_iterations || 10,
        termination_condition: template.termination_condition || {},
        input_schema: template.input_schema || {},
        output_schema: template.output_schema || {},
        metadata: template.metadata || {},
      });
      setPageMeta({
        itemName: template.name,
        description: "Configure the template's settings and behavior.",
      });
      originalDataRef.current = {
        name: template.name || "",
        agent_type: template.agent_type || "",
        agent_workflow_type: template.agent_workflow_type || "llm_agent",
        system_prompt: template.system_prompt || "",
        status: template.status || "active",
        output_key: template.output_key || "response",
        input_keys: template.input_keys || [],
        max_iterations: template.max_iterations || 10,
        termination_condition: template.termination_condition || {},
        input_schema: template.input_schema || {},
        output_schema: template.output_schema || {},
        metadata: template.metadata || {},
      };
    } catch (error) {
      console.error("Error fetching template:", error);
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddInputKey = () => {
    if (inputKeyText.trim() && !formData.input_keys.includes(inputKeyText.trim())) {
      setFormData((prev) => ({ ...prev, input_keys: [...prev.input_keys, inputKeyText.trim()] }));
      setInputKeyText("");
    }
  };

  const handleDeleteInputKey = (keyToDelete) => {
    setFormData((prev) => ({ ...prev, input_keys: prev.input_keys.filter((k) => k !== keyToDelete) }));
  };

  const handleSave = async () => {
    if (formData.agent_workflow_type === "loop_agent" && (!formData.max_iterations || formData.max_iterations < 1)) {
      toastError("Max iterations must be at least 1 for loop agents");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        agent_type: formData.agent_type,
        agent_workflow_type: formData.agent_workflow_type,
        system_prompt: formData.system_prompt,
        status: formData.status,
        output_key: formData.output_key,
        input_keys: formData.input_keys,
        input_schema: formData.input_schema,
        output_schema: formData.output_schema,
        metadata: formData.metadata,
      };
      if (formData.agent_workflow_type === "loop_agent") {
        payload.max_iterations = formData.max_iterations;
        payload.termination_condition = formData.termination_condition;
      }

      if (isEdit) {
        await makeRequest(`/v2/agent_templates/${id}`, { method: "PUT", body: { template: payload }, authTokens: tokens });
      } else {
        await makeRequest("/v2/agent_templates", { method: "POST", body: { template: payload }, authTokens: tokens });
      }
      navigate("/admin/agent-templates");
    } catch (error) {
      console.error("Error saving template:", error);
      toastError(error.response?.data?.error || error.message || "Failed to save template");
    } finally {
      setLoading(false);
    }
  };

  const isWorkflowAgent = ["sequential_agent", "parallel_agent", "loop_agent"].includes(formData.agent_workflow_type);

  if (fetchLoading) {
    return <LoadingSpinner message="Loading template..." subMessage="Please wait while we fetch the template details" />;
  }

  const isDirty = JSON.stringify(formData) !== JSON.stringify(originalDataRef.current);

  return (
    <Box
      component="form"
      onSubmit={(e) => { e.preventDefault(); handleSave(); }}
      sx={{ width: "100%", maxWidth: 720, pb: 10 }}
    >
      {/* Basic Information */}
      <SectionCard>
        <SectionHeader title="Basic Information" />

        <Box sx={{ mb: 2.5 }}>
          <Typography component="label" sx={FIELD_LABEL_SX}>
            Template Name <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
          </Typography>
          <TextField
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder="e.g., Customer Support Agent"
            fullWidth
            required
            size="small"
            sx={INPUT_SX}
          />
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography component="label" sx={FIELD_LABEL_SX}>
            Agent Type <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
          </Typography>
          <Typography sx={FIELD_HINT_SX}>Select the business/functional type of agent</Typography>
          <TextField
            select
            value={formData.agent_type}
            onChange={(e) => handleChange("agent_type", e.target.value)}
            fullWidth
            required
            size="small"
            sx={INPUT_SX}
          >
            {AGENT_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Box>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>{type.label}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: SLATE[500] }}>{type.description}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography component="label" sx={FIELD_LABEL_SX}>
            Agent Workflow Type <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
          </Typography>
          <Typography sx={FIELD_HINT_SX}>Choose the agent architecture type</Typography>
          <TextField
            select
            value={formData.agent_workflow_type}
            onChange={(e) => handleChange("agent_workflow_type", e.target.value)}
            fullWidth
            size="small"
            sx={INPUT_SX}
          >
            {WORKFLOW_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Box>
                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>{type.label}</Typography>
                  <Typography sx={{ fontSize: "0.75rem", color: SLATE[500] }}>{type.description}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box>
          <Typography component="label" sx={FIELD_LABEL_SX}>Status</Typography>
          <TextField
            select
            value={formData.status}
            onChange={(e) => handleChange("status", e.target.value)}
            size="small"
            sx={{ ...INPUT_SX, width: 200 }}
          >
            {STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </TextField>
        </Box>
      </SectionCard>

      {/* Agent Prompt */}
      <SectionCard>
        <SectionHeader title="Agent Prompt" />

        <Box>
          <Typography component="label" sx={FIELD_LABEL_SX}>
            System Prompt <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
          </Typography>
          <Typography sx={FIELD_HINT_SX}>Instructions that define the agent's behavior, personality, and capabilities</Typography>
          <PromptVariableAutocomplete
            value={formData.system_prompt}
            onChange={(e) => handleChange("system_prompt", e.target.value)}
            textFieldProps={{
              multiline: true,
              required: true,
              minRows: 6,
              maxRows: 16,
              placeholder: "You are a helpful assistant. Use {{variable_name}} to reference dynamic variables in your prompt...",
              fullWidth: true,
              sx: INPUT_SX,
            }}
          />
        </Box>
      </SectionCard>

      {/* Loop Agent Configuration */}
      {formData.agent_workflow_type === "loop_agent" && (
        <SectionCard>
          <SectionHeader title="Loop Agent Configuration" />

          <Box sx={{ mb: 2.5 }}>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Max Iterations <Box component="span" sx={{ color: "#ef4444" }}>*</Box>
            </Typography>
            <Typography sx={FIELD_HINT_SX}>Maximum number of times the loop will execute</Typography>
            <TextField
              type="number"
              value={formData.max_iterations}
              onChange={(e) => handleChange("max_iterations", parseInt(e.target.value))}
              inputProps={{ min: 1, max: 100 }}
              size="small"
              sx={{ ...INPUT_SX, width: 160 }}
            />
          </Box>

          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>Termination Condition (JSON)</Typography>
            <Typography sx={FIELD_HINT_SX}>Condition for early loop termination</Typography>
            <TextField
              multiline
              minRows={3}
              value={
                typeof formData.termination_condition === "object"
                  ? JSON.stringify(formData.termination_condition, null, 2)
                  : formData.termination_condition
              }
              onChange={(e) => {
                try {
                  handleChange("termination_condition", e.target.value ? JSON.parse(e.target.value) : {});
                } catch {
                  handleChange("termination_condition", e.target.value);
                }
              }}
              placeholder={'{\n  "type": "condition",\n  "field": "status",\n  "value": "complete"\n}'}
              fullWidth
              sx={{ ...INPUT_SX, "& .MuiInputBase-input": { fontFamily: "monospace", fontSize: "0.8125rem" } }}
            />
          </Box>
        </SectionCard>
      )}

      {/* Agent Composition */}
      <SectionCard>
        <SectionHeader
          title="Agent Composition"
          description="Attach capabilities and sub-agents after creating the template."
        />

        {isEdit ? (
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={<ToolIcon sx={{ fontSize: "1rem" }} />}
              onClick={() => navigate(`/admin/agent-templates/${id}/capabilities`)}
              sx={{ textTransform: "none", fontSize: "0.875rem", borderColor: SLATE[300], color: SLATE[700], "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}
            >
              Manage Capabilities
            </Button>
            {isWorkflowAgent && (
              <Button
                variant="outlined"
                startIcon={<SubAgentIcon sx={{ fontSize: "1rem" }} />}
                onClick={() => navigate(`/admin/agent-templates/${id}/sub-agents`)}
                sx={{ textTransform: "none", fontSize: "0.875rem", borderColor: SLATE[300], color: SLATE[700], "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}
              >
                Manage Sub-Agents
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ px: 2, py: 1.5, bgcolor: SLATE[50], borderRadius: "8px", border: `1px solid ${SLATE[200]}` }}>
            <Typography sx={{ fontSize: "0.8125rem", color: SLATE[600] }}>
              💡 You can attach capabilities{isWorkflowAgent ? " and sub-agents" : ""} after creating the template.
            </Typography>
          </Box>
        )}
      </SectionCard>

      {/* Input Keys (shown when editing) */}
      {isEdit && (
        <SectionCard>
          <SectionHeader title="Input Keys" description="Define the input keys this agent expects." />
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            {formData.input_keys.map((key) => (
              <Chip
                key={key}
                label={key}
                onDelete={() => handleDeleteInputKey(key)}
                size="small"
                sx={{ fontSize: "0.8125rem", bgcolor: SLATE[100], color: SLATE[700] }}
              />
            ))}
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              size="small"
              value={inputKeyText}
              onChange={(e) => setInputKeyText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddInputKey()}
              placeholder="Add input key..."
              sx={{ ...INPUT_SX, flex: 1 }}
            />
            <Button
              variant="outlined"
              onClick={handleAddInputKey}
              sx={{ textTransform: "none", fontSize: "0.875rem", borderColor: SLATE[300], color: SLATE[700], "&:hover": { borderColor: SLATE[400], bgcolor: SLATE[50] } }}
            >
              Add
            </Button>
          </Box>
        </SectionCard>
      )}

      <BottomActionBar
        name={isEdit ? "Save Changes" : "Create Template"}
        cancelRedirectUrl="/admin/agent-templates"
        saving={loading}
        isDirty={isDirty}
      />
    </Box>
  );
}
